/**
 * Generatorfunktion som itererar över alla element i en array ifall de inte filtreras bort och sedan applicerar en map-funktion på dem.
 * @param {array} array 
 * @param {function} [filterFunction]
 * @param {function} mapFunction 
 * @param {function} [postFilterFunction]
 * @returns {*}
 */
export function* mapArrayGenerator(array, filterFunction, mapFunction, postFilterFunction) {
    if (!array) {
        return;
    }

    let idx = 0;

    for (const value of array) {
        if (filterFunction && !filterFunction(value)) {
            continue;
        }

        const result = mapFunction(value, idx++);
        
        if (postFilterFunction && !postFilterFunction(result)) {
            continue;
        }

        yield result;
    }
}

/**
 * Generatorfunktion som itererar över alla element i en array i omvänd ordning ifall de inte filtreras bort och sedan applicerar en map-funktion på dem.
 * @param {array} array 
 * @param {function} [filterFunction]
 * @param {function} mapFunction 
 * @param {function} [postFilterFunction]
 * @returns {*}
 */
export function* reverseMapArrayGenerator(array, filterFunction, mapFunction, postFilterFunction) {
    if (!array) {
        return;
    }

    let idx = 0;

    for (let i = array.length - 1; i >= 0; i--) {
        const value = array[i];

        if (filterFunction && !filterFunction(value)) {
            continue;
        }

        const result = mapFunction(value, idx++);
        
        if (postFilterFunction && !postFilterFunction(result)) {
            continue;
        }

        yield result;
    }
}

/**
 * Applicerar mapFunction på alla värden i en array som inte filtrerats bort.
 * @param {array} array 
 * @param {function} [filterFunction]
 * @param {function} mapFunction
 * @param {function} [postFilterFunction]
 * @returns {Array}
 */
export function filterMap(array, filterFunction, mapFunction, postFilterFunction) {
    return Array.from(mapArrayGenerator(array, filterFunction, mapFunction, postFilterFunction));
}

/**
 * Skapar en iterator över resultatet att anropa mapFunction på alla värden i en array som inte filtrerats bort.
 * @param {array} array 
 * @param {function} [filterFunction]
 * @param {function} mapFunction
 * @param {function} [postFilterFunction]
 * @returns {iterator}
 */
export function filterMapIterator(array, filterFunction, mapFunction, postFilterFunction) {
    return {
        *[Symbol.iterator]() {
            yield* mapArrayGenerator(array, filterFunction, mapFunction, postFilterFunction);
        }
    };
}

/**
 * Applicerar mapFunction på alla värden i en array som inte filtrerats bort i omvänd ordning.
 * @param {array} array 
 * @param {function} [filterFunction]
 * @param {function} mapFunction 
 * @param {function} [postFilterFunction]
 * @returns {Array}
 */
export function reverseMap(array, filterFunction, mapFunction, postFilterFunction) {
    return Array.from(reverseMapArrayGenerator(array, filterFunction, mapFunction, postFilterFunction));
}

/**
 * Counts the number of elements in an array for which the supplied function returns true.
 * @param {*[]} array0 - An array of elements.
 * @param {function(*)} test - A function which will be passed every element in the array.
 * @return {number}
 */
export function countMatches(array0, test) {
    return array0.reduce((count, current) => test(current) ? count + 1 : count, 0);
}

/**
 * Sorts an array with multiple functions, if one function returns 0 then the next function will be tested.
 * This is needed, because sort() is not guaranteed to be stable.
 * @param {Array} array0 - An array of elements.
 * @param {Array} arrayOfSortFunctions - An array of functions that will be used for sorting
 * @return {undefined}
 */
export function sortArrayBy(array0, arrayOfSortFunctions) {
    array0.sort( (a,b) => {
        let result = 0;
        for (let i = 0; i < arrayOfSortFunctions.length && result == 0; i++) {
            result = arrayOfSortFunctions[i](a,b);
        }
        return result;
    });
}

/**
 * Checks if two arrays are shallowly equal (=== on all elements and same length).
 * @param {Array} array0 
 * @param {Array} array1 
 * @returns {boolean}
 */
export function arraysAreShallowlyEqual(array0, array1) {
    if (array0 === array1) {
        return true;
    } else if ((array0 && !array1) || (!array0 && array1)) {
        return false;
    }

    if (array0.length !== array1.length) {
        return false;
    }

    for (const idx in array0) {
        if (!Object.is(array0[idx], array1[idx])) {
            return false;
        }
    }

    return true;
}

const EMPTY_ARRAY = [];

/**
 * Returns a constant empty array.
 * @returns {Array}
 */
export function getConstEmptyArray() {
    if (process.env.NODE_ENV !== "production") {
        if (EMPTY_ARRAY.length !== 0) {
            throw new Error("EMPTY_ARRAY has been changed");
        }
    }
    return EMPTY_ARRAY;
}

/**
 * Returns the last element of an array. null if empty.
 * @param {Array} array 
 * @returns {*}
 */
export function lastElement(array) {
    if (!array) {
        return array;
    } else if (array.length > 0) {
        return array[array.length - 1];
    } else {
        return null;
    }
}

/**
 * 
 * @param {*} object 
 * @param  {...any} path 
 * @returns {*}
 */
export function getIn(object, ...path) {
    let root = object;
    for (const key of path) {
        if (root == null) {
            break;
        }
        
        const val = root[key];
        if (val == null) {
            root = null;
            break;
        } else {
            root = val;
        }
    }
    return root;
}

/**
 * Utility that checks if all values in array are equal. Returns true or false
 * @param {*} arr 
 * @returns {boolean}
 */
export function allValuesEqual(arr){
    return arr.every( v => v === arr[0]);
}